/**
 * CheckpointBanner — Persistent banner for session interruptions.
 *
 * UX Spec Section 5.2.10 / Story 1.6 AC#7.
 *
 * Types: connection_lost, hitl_gate, processing_error, session_expired.
 * Persists until user acts — Escape does NOT dismiss.
 * role="alertdialog" with aria-live="assertive".
 */

import { useEffect, useRef } from "react";
import { AlertTriangle, Plug, RotateCw, XCircle } from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type BannerType =
  | "connection_lost"
  | "hitl_gate"
  | "processing_error"
  | "session_expired";

export interface CheckpointBannerProps {
  type: BannerType;
  message: string;
  /** Primary action */
  primaryAction: { label: string; onClick: () => void };
  /** Secondary action */
  secondaryAction?: { label: string; onClick: () => void };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const BANNER_CONFIG: Record<
  BannerType,
  { icon: typeof AlertTriangle; borderColor: string; bgColor: string }
> = {
  connection_lost: {
    icon: Plug,
    borderColor: "var(--readiness-warning)",
    bgColor: "var(--readiness-warning)",
  },
  hitl_gate: {
    icon: AlertTriangle,
    borderColor: "var(--readiness-warning)",
    bgColor: "var(--readiness-warning)",
  },
  processing_error: {
    icon: XCircle,
    borderColor: "var(--destructive)",
    bgColor: "var(--destructive)",
  },
  session_expired: {
    icon: RotateCw,
    borderColor: "var(--muted-foreground)",
    bgColor: "var(--muted-foreground)",
  },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CheckpointBanner({
  type,
  message,
  primaryAction,
  secondaryAction,
}: CheckpointBannerProps) {
  const bannerRef = useRef<HTMLDivElement>(null);
  const config = BANNER_CONFIG[type];
  const Icon = config.icon;

  // Focus banner on mount
  useEffect(() => {
    bannerRef.current?.focus();
  }, []);

  return (
    <div
      ref={bannerRef}
      role="alertdialog"
      aria-live="assertive"
      aria-label={message}
      tabIndex={-1}
      className="flex items-center gap-3 px-4 py-3 border-l-4 outline-none"
      style={{
        borderLeftColor: config.borderColor,
        backgroundColor: `color-mix(in srgb, ${config.bgColor} 8%, transparent)`,
      }}
    >
      <Icon
        className="w-5 h-5 shrink-0"
        style={{ color: config.borderColor }}
      />

      <p className="flex-1 text-body text-[var(--foreground)]">{message}</p>

      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={primaryAction.onClick}
          className="px-3 py-1.5 rounded-lg text-caption font-medium text-white"
          style={{ backgroundColor: config.borderColor }}
        >
          {primaryAction.label}
        </button>
        {secondaryAction && (
          <button
            onClick={secondaryAction.onClick}
            className="px-3 py-1.5 rounded-lg text-caption font-medium border text-[var(--foreground)] hover:bg-white/5"
            style={{ borderColor: config.borderColor }}
          >
            {secondaryAction.label}
          </button>
        )}
      </div>
    </div>
  );
}
